import { inject, Injectable } from "@angular/core";
import {
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  FacebookAuthProvider,
  getAuth,
  GoogleAuthProvider,
  inMemoryPersistence,
  OAuthProvider,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  UserCredential,
} from "firebase/auth";
import { getApps, initializeApp } from "firebase/app";
import { environment } from "../../environments/environment";
import { AuthConfigurationService } from "./auth-configuration.service";

@Injectable({ providedIn: "root" })
export class AuthService {
  private readonly configuration = inject(AuthConfigurationService);
  private readonly auth = this.initFirebaseAuth();
  readonly providerToggles = this.configuration.providerToggles;

  private initFirebaseAuth() {
    // TODO: In dev, use auth-ui-web-dev, in Prod use auth-ui-web-prod
    const existingApp = getApps().find((app) => app.name === "auth-ui-web");
    const app = existingApp ?? initializeApp(environment.firebase, "auth-ui-web");
    const auth = getAuth(app);
    auth.useDeviceLanguage();
    auth.tenantId = null;
    void setPersistence(auth, browserLocalPersistence).catch(async () => {
      await setPersistence(auth, inMemoryPersistence);
    });
    return auth;
  }

  signInWithEmailPassword(email: string, password: string) {
    this.clearFirebaseTenantContext();
    return signInWithEmailAndPassword(this.auth, email, password);
  }

  createAccountWithEmailPassword(email: string, password: string) {
    this.clearFirebaseTenantContext();
    return createUserWithEmailAndPassword(this.auth, email, password);
  }

  signInWithGoogle() {
    this.clearFirebaseTenantContext();
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    return signInWithPopup(this.auth, provider);
  }

  signInWithFacebook() {
    this.clearFirebaseTenantContext();
    const provider = new FacebookAuthProvider();
    provider.setCustomParameters({ display: "popup" });
    return signInWithPopup(this.auth, provider);
  }

  signInWithApple() {
    this.clearFirebaseTenantContext();
    const provider = new OAuthProvider("apple.com");
    provider.addScope("email");
    provider.addScope("name");
    return signInWithPopup(this.auth, provider);
  }

  async finalizeSignIn(credential: UserCredential, redirectUrl: string | null, state: string | null) {
    const idToken = await credential.user.getIdToken();
    const refreshToken = credential.user.refreshToken;
    if (redirectUrl) {
      const target = new URL(redirectUrl);
      const params = new URLSearchParams({ idToken, refreshToken });
      if (state) {
        params.set("state", state);
      }
      target.hash = params.toString();
      window.location.replace(target.toString());
      return;
    }
    if (window.opener) {
      const message = {
        source: "trump-post-alert-auth-ui",
        idToken,
        refreshToken,
        state: state ?? undefined,
      };
      window.opener.postMessage(message, "*");
      window.close();
      return;
    }
    console.info("Login completed without redirect target. Token available in memory.");
  }

  validateRedirectUrl(rawUrl: string | null): string | null {
    if (!rawUrl) {
      return null;
    }
    try {
      const url = new URL(rawUrl);
      if (url.protocol === "https:") {
        return url.toString();
      }
      if (
        url.protocol === "http:" &&
        this.configuration.allowHttpOnLocalhost &&
        ["localhost", "127.0.0.1", "0.0.0.0"].includes(url.hostname)
      ) {
        return url.toString();
      }
      const currentOrigin = typeof window !== "undefined" ? window.location.origin : "";
      if (currentOrigin && url.origin === currentOrigin) {
        return url.toString();
      }
      if (this.configuration.allowedRedirectHosts().includes(url.hostname)) {
        return url.toString();
      }
    } catch (error) {
      console.warn("Invalid redirect URL provided", error);
      return null;
    }
    return null;
  }

  private clearFirebaseTenantContext() {
    this.auth.tenantId = null;
  }
}
